import type { FacilityType } from "@klyra/shared";
import { specialtyProfile } from "@klyra/shared";
import { Link } from "@tanstack/react-router";
import {
	CalendarPlus,
	FlaskConical,
	Plus,
	Stethoscope,
	UserPlus,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface CreateItem {
	to: "/appointments/new" | "/patients/new" | "/walk-in" | "/clinic-walk-in";
	icon: typeof Plus;
	label: string;
}

/**
 * Header "+" quick-create. Puts the facility's most common create actions one
 * tap away from ANY page (not just the dashboard) — the main lever against
 * "simple things take too many pages". Facility-aware; closes on outside
 * click, Escape, or navigation. Sits in the top bar (not a floating button)
 * to avoid colliding with the voice-assistant orb.
 */
export function QuickCreateMenu({
	facilityType,
	businessType,
}: {
	facilityType: FacilityType | undefined | null;
	businessType?: string | null;
}) {
	const { t } = useTranslation("common");
	// Aesthetic/therapy specialties deliver "sessions", not "visits".
	const isSession = specialtyProfile(businessType).encounterNoun === "session";
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		const onDown = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node))
				setOpen(false);
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setOpen(false);
		};
		document.addEventListener("mousedown", onDown);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDown);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	const items: CreateItem[] =
		facilityType === "lab"
			? [
					{
						to: "/walk-in",
						icon: FlaskConical,
						label: t("quick_create.walk_in", { defaultValue: "فحص مباشر" }),
					},
					{
						to: "/patients/new",
						icon: UserPlus,
						label: t("quick_create.patient", { defaultValue: "مراجع جديد" }),
					},
				]
			: facilityType === "radiology" || facilityType === "pharmacy"
				? [
						{
							to: "/patients/new",
							icon: UserPlus,
							label: t("quick_create.patient", { defaultValue: "مراجع جديد" }),
						},
					]
				: [
						{
							to: "/clinic-walk-in",
							icon: Stethoscope,
							label: t("quick_create.walk_in_visit", {
								defaultValue: "مراجع بدون موعد",
							}),
						},
						{
							to: "/appointments/new",
							icon: CalendarPlus,
							label: isSession
								? t("quick_create.session", { defaultValue: "جلسة جديدة" })
								: t("quick_create.appointment", {
										defaultValue: "موعد جديد",
									}),
						},
						{
							to: "/patients/new",
							icon: UserPlus,
							label: t("quick_create.patient", { defaultValue: "مريض جديد" }),
						},
					];

	return (
		<div className="relative" ref={ref}>
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				className="inline-flex items-center justify-center size-10 sm:size-9 rounded-lg bg-brand-600 hover:bg-brand-700 text-white transition-colors"
				aria-label={t("quick_create.title", { defaultValue: "إنشاء سريع" })}
				aria-haspopup="menu"
				aria-expanded={open}
			>
				<Plus className="size-5" strokeWidth={2.5} aria-hidden />
			</button>

			{open && (
				<div
					role="menu"
					className="absolute end-0 mt-2 w-52 rounded-xl border border-border bg-card shadow-elevated p-1.5 z-50 animate-pop-in"
				>
					<p className="px-2.5 py-1.5 text-xs uppercase text-muted-foreground">
						{t("quick_create.title", { defaultValue: "إنشاء سريع" })}
					</p>
					{items.map((it) => {
						const Icon = it.icon;
						return (
							<Link
								key={it.to}
								to={it.to}
								role="menuitem"
								onClick={() => setOpen(false)}
								className="flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-sm text-foreground hover:bg-muted transition-colors min-h-[44px]"
							>
								<span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-950/40 dark:text-brand-300">
									<Icon className="size-4" strokeWidth={2} aria-hidden />
								</span>
								<span className="font-medium">{it.label}</span>
							</Link>
						);
					})}
				</div>
			)}
		</div>
	);
}
